<template>
  <AppLayout :breadcrumbs="[{ label: 'Receivables', link: '/receivables' }]">
    <div class="layout-header">
      <div class="header-left">
        <SearchBar v-model="searchTerm" @update:modelValue="handleSearch" placeholder="Search receivables..." />
        <FilterSelector :modelValue="selectedFilters" @update:modelValue="handleFilterChange" :field-options="fieldOptions" />
      </div>
      <div class="header-right">
        <button class="button icon-button" @click="openColumnSettingsModal" title="Column Settings">
          <CogIcon :size="18" />
        </button>
        <button class="button" @click="openExportModal" :disabled="totalReceivables === 0">
          Export
        </button>
      </div>
    </div>

    <div class="layout-body">
      <CustomTable :columns="visibleColumnsFiltered" :rows="receivables" :sticky-first-column="true" :loading="isLoading"
        :is-empty="receivables.length === 0" :sort-by="sortBy" :sort-order="sortOrder" @row-click="handleRowClick" @sort="handleSort">
        <template #column-receivable="{ row }">
          <span class="cell-value name-text">Receivable #{{ row.receivableId }}</span>
        </template>

        <template #column-dateDue="{ row }">
          <span class="cell-value">{{ formatUtils.formatDate(row.dateDue) }}</span>
        </template>

        <template #column-patient="{ row }">
          <div class="patient-cell">
            <IconCircle :size="28" bgColor="var(--icon-patient-bg)" color="var(--icon-patient-color)"
              :initials="getPatientInitials(row)" />
            <span class="cell-value">{{ getPatientName(row) }}</span>
          </div>
        </template>

        <template #column-ticket="{ row }">
          <span class="cell-value">Ticket #{{ row.ticketId }}</span>
        </template>

        <template #column-status="{ row }">
          <StatusBadge :status="calculateReceivableStatus(row)" />
        </template>

        <template #column-amountDue="{ row }">
          <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCents) }}</span>
        </template>

        <template #column-collected="{ row }">
          <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsCollected) }}</span>
        </template>

        <template #column-cancelled="{ row }">
          <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsCancelled) }}</span>
        </template>

        <template #column-balance="{ row }">
          <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsBalance) }}</span>
        </template>

        <template #column-actions="{ row }">
          <button
            class="button button-small"
            @click.stop="openModal('create-payment', { receivable: row })"
            :disabled="row.amountCentsBalance === 0">
            Process Payment
          </button>
        </template>

      </CustomTable>
    </div>

    <div class="layout-footer">
      <PaginationControl :current-page="currentPage" :total-items="totalReceivables" :items-per-page="itemsPerPage"
        @page-change="handlePageChange" />
    </div>

    <!-- Modals -->
    <transition name="modal-fade" mode="out-in">
      <div class="modal-background close-on-click" v-if="modal.open" @click="handleModalClick">
        <div class="modal-scroll close-on-click">
          <transition name="modal-pop" mode="out-in">
            <CreatePaymentModal
              v-if="modal.id === 'create-payment'"
              :receivable="modal.data.receivable"
              @payment-created="handlePaymentCreated"
            />
            <ExportDataModal
              v-else-if="modal.id === 'export'"
              entity-type="receivables"
              :columns="columns"
              :current-page-count="receivables.length"
              :total-count="totalReceivables"
              :export-config="exportConfig"
              :is-exporting="isExporting"
              :export-progress="exportProgress"
              :error="exportError"
              @export="handleExport"
            />
            <ColumnSettingsModal
              v-else-if="modal.id === 'columnSettings'"
              :columns="columns"
              :storage-key="`shawncrm_columns_${practiceId}_receivables`"
              :initial-visibility="columnVisibility"
              @update:visibility="handleColumnVisibilityChange"
            />
          </transition>
        </div>
      </div>
    </transition>
  </AppLayout>
</template>

<script>
import AppLayout from '../../components/app/AppLayout.vue';
import SearchBar from '../../components/common/SearchBar.vue';
import FilterSelector from '../../components/common/FilterSelector.vue';
import CustomTable from '../../components/common/CustomTable.vue';
import PaginationControl from '../../components/common/PaginationControl.vue';
import IconCircle from '@/components/common/IconCircle.vue';
import StatusBadge from '@/components/common/StatusBadge.vue';
import CreatePaymentModal from '@/modals/CreatePaymentModal.vue';
import ExportDataModal from '../../modals/ExportDataModal.vue';
import ColumnSettingsModal from '../../modals/ColumnSettingsModal.vue';
import CogIcon from 'vue-material-design-icons/Cog.vue';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';
import * as api from '@/api';
import * as formatUtils from '@/utils/formatters';
import { calculateReceivableStatus } from '@/utils/formatters';
import { generateCSV, downloadCSV } from '@/utils/csvExport';
import { serializeFilters, deserializeFilters } from '@/utils/filterSerializer';
import { getLocalToday } from '@/utils/dateUtils';


export default {
  name: 'ReceivableListView',
  data() {
    return {
      searchTerm: '',
      itemsPerPage: 100,
      currentPage: 1,
      totalReceivables: 0,
      receivables: [],
      isLoading: false,
      sortBy: 'dateDue',
      sortOrder: 'desc',
      selectedFilters: [],
      columnVisibility: {},
      // Modal state (shared with CreatePaymentModal)
      modal: {
        open: false,
        id: '',
        data: {}
      },
      // Export state
      isExporting: false,
      exportProgress: { current: 0, total: 0 },
      exportError: null,
      fieldOptions: [
        {
          id: 'dateDue',
          label: 'Date Due',
          type: 'date',
          apiParams: {
            from: 'dateDueAfter',
            to: 'dateDueBefore'
          }
        },
        {
          id: 'amountCents',
          label: 'Amount Due',
          type: 'currency',
          apiParams: {
            from: 'amountCentsGte',
            to: 'amountCentsLte'
          }
        },
        {
          id: 'amountCentsCollected',
          label: 'Amount Collected',
          type: 'currency',
          apiParams: {
            from: 'amountCentsCollectedGte',
            to: 'amountCentsCollectedLte'
          }
        },
        {
          id: 'amountCentsCancelled',
          label: 'Amount Cancelled',
          type: 'currency',
          apiParams: {
            from: 'amountCentsCancelledGte',
            to: 'amountCentsCancelledLte'
          }
        },
        {
          id: 'amountCentsBalance',
          label: 'Current Balance',
          type: 'currency',
          apiParams: {
            from: 'amountCentsBalanceGte',
            to: 'amountCentsBalanceLte'
          }
        }
      ],
      columns: [
        { key: 'receivable', label: 'Receivable ID', width: '200px' },
        { key: 'dateDue', label: 'Date Due', width: '200px', sortable: true, sortKey: 'dateDue' },
        { key: 'patient', label: 'Patient', width: '200px' },
        { key: 'ticket', label: 'Ticket', width: '200px' },
        { key: 'status', label: 'Status', width: '200px' },
        { key: 'amountDue', label: 'Amount Due', width: '200px', sortable: true, sortKey: 'amountCents', description: 'Original scheduled payment amount for this receivable' },
        { key: 'collected', label: 'Amount Collected', width: '200px', sortable: true, sortKey: 'amountCentsCollected', description: 'Total amount successfully collected from payments' },
        { key: 'cancelled', label: 'Amount Cancelled', width: '200px', sortable: true, sortKey: 'amountCentsCancelled', description: 'Amount cancelled if this receivable was cancelled' },
        { key: 'balance', label: 'Current Balance', width: '200px', sortable: true, sortKey: 'amountCentsBalance', description: 'Amount still owed: amount initially due minus amount paid minus amount cancelled' },
        { key: 'actions', label: '', width: '180px' }
      ],
      // Export column mapping (excluding actions column)
      exportConfig: {
        receivable: {
          label: 'Receivable ID',
          format: (row) => `Receivable #${row.receivableId}`
        },
        dateDue: {
          label: 'Date Due',
          format: (row) => formatUtils.formatDate(row.dateDue)
        },
        patient: {
          label: 'Patient',
          format: (row) => `${row.patientFirstName || ''} ${row.patientLastName || ''}`.trim() || 'Unknown'
        },
        ticket: {
          label: 'Ticket',
          format: (row) => `Ticket #${row.ticketId}`
        },
        status: {
          label: 'Status',
          format: (row) => {
            const status = calculateReceivableStatus(row);
            if (!status) return 'Unknown';
            return status.charAt(0).toUpperCase() + status.slice(1);
          }
        },
        amountDue: {
          label: 'Amount Due',
          format: (row) => formatUtils.formatCurrency(row.amountCents)
        },
        collected: {
          label: 'Amount Collected',
          format: (row) => formatUtils.formatCurrency(row.amountCentsCollected)
        },
        cancelled: {
          label: 'Amount Cancelled',
          format: (row) => formatUtils.formatCurrency(row.amountCentsCancelled)
        },
        balance: {
          label: 'Current Balance',
          format: (row) => formatUtils.formatCurrency(row.amountCentsBalance)
        }
      },
      formatUtils,
    };
  },
  computed: {
    practiceId() {
      return api.getPracticeId();
    },
    visibleColumnsFiltered() {
      return this.columns.filter(col => {
        // Always show first column (receivable)
        if (col.key === 'receivable') return true;
        // Always show actions column
        if (col.key === 'actions') return true;
        // Show column if visibility is not set (default true) or explicitly true
        return this.columnVisibility[col.key] !== false;
      });
    }
  },
  watch: {
    '$route.query': {
      handler(newQuery, oldQuery) {
        if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return;

        const urlFilters = deserializeFilters(newQuery.filters);
        const urlSearch = newQuery.search || '';
        const urlPage = parseInt(newQuery.page) || 1;
        const urlSortBy = newQuery.sortBy || 'dateDue';
        const urlSortOrder = newQuery.sortOrder || 'desc';

        const hasChanges =
          JSON.stringify(urlFilters) !== JSON.stringify(this.selectedFilters) ||
          urlSearch !== this.searchTerm ||
          urlPage !== this.currentPage ||
          urlSortBy !== this.sortBy ||
          urlSortOrder !== this.sortOrder;

        if (!hasChanges) return;

        this.selectedFilters = urlFilters;
        this.searchTerm = urlSearch;
        this.currentPage = urlPage;
        this.sortBy = urlSortBy;
        this.sortOrder = urlSortOrder;

        this.loadReceivables();
      }
    }
  },
  created() {
    // Check if URL has any state params
    const hasUrlParams =
      this.$route.query.filters !== undefined ||
      this.$route.query.search !== undefined ||
      this.$route.query.sortBy !== undefined ||
      this.$route.query.sortOrder !== undefined;

    if (hasUrlParams) {
      // Load from URL (existing logic)
      if (this.$route.query.filters !== undefined) {
        this.selectedFilters = deserializeFilters(this.$route.query.filters);
      }
      if (this.$route.query.search !== undefined) {
        this.searchTerm = this.$route.query.search || '';
      }
      if (this.$route.query.sortBy !== undefined) {
        this.sortBy = this.$route.query.sortBy || 'dateDue';
      }
      if (this.$route.query.sortOrder !== undefined) {
        this.sortOrder = this.$route.query.sortOrder || 'desc';
      }
    } else {
      // Load from localStorage
      const savedState = this.loadFiltersFromLocalStorage();
      if (savedState) {
        this.selectedFilters = savedState.selectedFilters || [];
        this.sortBy = savedState.sortBy || 'dateDue';
        this.sortOrder = savedState.sortOrder || 'desc';

        // Immediately sync to URL
        this.syncStateToUrl();
      }
    }

    this.currentPage = parseInt(this.$route.query.page) || 1;

    // Load column visibility settings
    this.loadColumnVisibility();

    this.loadReceivables();
  },
  methods: {
    getFilterParams() {
      const params = {};

      this.selectedFilters.forEach(filter => {
        const fieldConfig = this.fieldOptions.find(f => f.id === filter.field);
        if (!fieldConfig || !filter.operator) return;

        const fieldType = fieldConfig.type;

        if (fieldType === 'date' || fieldType === 'currency') {
          if (filter.operator === 'between') {
            if (filter.from !== null && filter.from !== undefined && filter.from !== '') {
              params[fieldConfig.apiParams.from] = this.formatParamValue(filter.from, fieldType);
            }
            if (filter.to !== null && filter.to !== undefined && filter.to !== '') {
              params[fieldConfig.apiParams.to] = this.formatParamValue(filter.to, fieldType);
            }
          } else if (filter.operator === 'after' || filter.operator === 'gte') {
            if (filter.from !== null && filter.from !== undefined && filter.from !== '') {
              params[fieldConfig.apiParams.from] = this.formatParamValue(filter.from, fieldType);
            }
          } else if (filter.operator === 'before' || filter.operator === 'lte') {
            if (filter.to !== null && filter.to !== undefined && filter.to !== '') {
              params[fieldConfig.apiParams.to] = this.formatParamValue(filter.to, fieldType);
            }
          } else if (filter.operator === 'is' && fieldType === 'date') {
            if (filter.value !== null && filter.value !== undefined && filter.value !== '') {
              const formattedValue = this.formatParamValue(filter.value, fieldType);
              params[fieldConfig.apiParams.from] = formattedValue;
              params[fieldConfig.apiParams.to] = formattedValue;
            }
          }
        }
      });

      return params;
    },
    formatParamValue(value, fieldType) {
      if (value === null || value === undefined || value === '') return null;

      if (fieldType === 'date') {
        return value;
      } else if (fieldType === 'currency') {
        return value;
      }

      return value;
    },
    syncStateToUrl() {
      const query = {};

      const serialized = serializeFilters(this.selectedFilters);
      if (serialized) {
        query.filters = serialized;
      }

      if (this.searchTerm) {
        query.search = this.searchTerm;
      }

      if (this.currentPage > 1) {
        query.page = this.currentPage;
      }

      if (this.sortBy !== 'dateDue') {
        query.sortBy = this.sortBy;
      }

      if (this.sortOrder !== 'desc') {
        query.sortOrder = this.sortOrder;
      }

      this.$router.replace({ query }).catch(() => {});
    },
    async loadReceivables() {
      try {
        this.isLoading = true;

        const offset = (this.currentPage - 1) * this.itemsPerPage;
        const filterParams = this.getFilterParams();

        const response = await queryClient.fetchQuery({
          queryKey: ['receivables', {
            limit: this.itemsPerPage,
            offset,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            ...filterParams
          }],
          queryFn: () => api.fetchReceivables({
            limit: this.itemsPerPage,
            offset,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            ...filterParams
          })
        });

        this.receivables = response.receivables || [];
        this.totalReceivables = response.total || 0;

      } catch (err) {
        console.error('Failed to fetch receivables:', err);
        alertService.error('Failed to load receivables: ' + err.message);
        this.receivables = [];
        this.totalReceivables = 0;
      } finally {
        this.isLoading = false;
      }
    },
    handleSearch() {
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadReceivables();
    },
    handleFilterChange(filters) {
      this.selectedFilters = filters;
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadReceivables();
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.syncStateToUrl();
      this.loadReceivables();
    },
    handleRowClick({ rowIndex }) {
      const receivable = this.receivables[rowIndex];
      if (receivable && receivable.ticketId) {
        this.$router.push(`/tickets/${receivable.ticketId}/receivables`);
      }
    },
    handleSort({ sortBy, sortOrder }) {
      this.sortBy = sortBy;
      this.sortOrder = sortOrder;
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadReceivables();
    },
    saveFiltersToLocalStorage() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        // Store filters as serialized string (same as URL)
        const serialized = serializeFilters(this.selectedFilters);
        if (serialized) {
          localStorage.setItem(`shawncrm_filters_${practiceId}_receivables`, serialized);
        } else {
          localStorage.removeItem(`shawncrm_filters_${practiceId}_receivables`);
        }

        // Store sort settings
        localStorage.setItem(`shawncrm_sortBy_${practiceId}_receivables`, this.sortBy);
        localStorage.setItem(`shawncrm_sortOrder_${practiceId}_receivables`, this.sortOrder);
      } catch (err) {
        console.error('Failed to save filters to localStorage:', err);
      }
    },
    loadFiltersFromLocalStorage() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return null;

      try {
        const filtersStr = localStorage.getItem(`shawncrm_filters_${practiceId}_receivables`);
        const sortBy = localStorage.getItem(`shawncrm_sortBy_${practiceId}_receivables`);
        const sortOrder = localStorage.getItem(`shawncrm_sortOrder_${practiceId}_receivables`);

        return {
          selectedFilters: filtersStr ? deserializeFilters(filtersStr) : [],
          sortBy: sortBy || 'dateDue',
          sortOrder: sortOrder || 'desc'
        };
      } catch (err) {
        console.error('Failed to load filters from localStorage:', err);
        return null;
      }
    },
    getPatientInitials(receivable) {
      const firstName = receivable.patientFirstName || '';
      const lastName = receivable.patientLastName || '';
      return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'U';
    },
    getPatientName(receivable) {
      const firstName = receivable.patientFirstName || '';
      const lastName = receivable.patientLastName || '';
      return `${firstName} ${lastName}`.trim() || 'Unknown';
    },
    loadColumnVisibility() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        const saved = localStorage.getItem(`shawncrm_columns_${practiceId}_receivables`);
        if (saved) {
          this.columnVisibility = JSON.parse(saved);
        } else {
          // Default columns: Date Due, Patient, Ticket, Status, Amount Due, Amount Collected, Current Balance
          const defaultVisibleColumns = ['dateDue', 'patient', 'ticket', 'status', 'amountDue', 'collected', 'balance'];
          this.columns.forEach(col => {
            this.columnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
          });
        }
      } catch (err) {
        console.error('Failed to load column visibility:', err);
        // Default columns on error
        const defaultVisibleColumns = ['dateDue', 'patient', 'ticket', 'status', 'amountDue', 'collected', 'balance'];
        this.columns.forEach(col => {
          this.columnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
        });
      }
    },
    saveColumnVisibility() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        localStorage.setItem(
          `shawncrm_columns_${practiceId}_receivables`,
          JSON.stringify(this.columnVisibility)
        );
      } catch (err) {
        console.error('Failed to save column visibility:', err);
      }
    },
    handleColumnVisibilityChange(visibility) {
      this.columnVisibility = visibility;
      this.saveColumnVisibility();
    },
    // Modal management
    openColumnSettingsModal() {
      this.openModal('columnSettings', {});
    },
    openExportModal() {
      this.openModal('export', {});
    },
    openModal(id, data) {
      this.modal.open = true;
      setTimeout(() => {
        this.modal.id = id;
        this.modal.data = data;
      });
    },
    closeModal() {
      this.modal.id = '';
      setTimeout(() => {
        this.modal.open = false;
      });
    },
    handleModalClick(event) {
      const classList = event.target.classList;
      if (classList.contains('close-on-click')) {
        this.closeModal();
      }
    },
    handlePaymentCreated() {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      this.loadReceivables();
      alertService.success('Payment processed successfully');
    },
    calculateReceivableStatus(receivable) {
      return calculateReceivableStatus(receivable);
    },
    // Export modal methods
    async handleExport({ scope, columns }) {
      this.isExporting = true;
      this.exportError = null;
      this.exportProgress = { current: 0, total: 0 };

      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        let dataToExport = [];

        if (scope === 'current') {
          dataToExport = this.receivables;
          this.exportProgress = { current: this.receivables.length, total: this.receivables.length };
        } else {
          const filterParams = this.getFilterParams();
          const pageSize = 100;

          const firstPageResponse = await api.fetchReceivables({
            limit: pageSize,
            offset: 0,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            ...filterParams
          });

          const totalRecords = firstPageResponse.total;
          const totalPages = Math.ceil(totalRecords / pageSize);

          this.exportProgress = { current: 0, total: totalRecords };
          await this.$nextTick();

          dataToExport = [...firstPageResponse.receivables];
          this.exportProgress.current = firstPageResponse.receivables.length;
          await this.$nextTick();

          for (let page = 1; page < totalPages; page++) {
            await new Promise(resolve => setTimeout(resolve, 100));

            const offset = page * pageSize;
            const response = await api.fetchReceivables({
              limit: pageSize,
              offset,
              search: this.searchTerm,
              sortBy: this.sortBy,
              sortOrder: this.sortOrder,
              ...filterParams
            });

            dataToExport = [...dataToExport, ...response.receivables];
            this.exportProgress.current = dataToExport.length;
            await this.$nextTick();
          }
        }

        const csvContent = generateCSV(dataToExport, columns, this.exportConfig);

        const timestamp = getLocalToday();
        const filename = `receivables-export-${timestamp}`;
        downloadCSV(csvContent, filename);

        alertService.success(`Successfully exported ${dataToExport.length} receivables`);

        // Close modal after short delay to show completion
        setTimeout(() => {
          this.closeModal();
          this.isExporting = false;
          this.exportProgress = { current: 0, total: 0 };
        }, 500);

      } catch (err) {
        console.error('Export failed:', err);
        this.exportError = err.message || 'Failed to export data. Please try again.';
        this.isExporting = false;
      }
    }
  },
  components: {
    AppLayout,
    SearchBar,
    FilterSelector,
    CustomTable,
    PaginationControl,
    IconCircle,
    StatusBadge,
    CreatePaymentModal,
    ExportDataModal,
    ColumnSettingsModal,
    CogIcon
  }
}
</script>

<style scoped>
/* Header section */
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-left> :first-child {
  max-width: 300px;
  flex: 1;
  min-width: 200px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.layout-header .button {
  white-space: nowrap;
}

.icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  min-width: 32px;
}

/* Cell styling */
.receivable-cell,
.patient-cell,
.ticket-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
}

.name-text {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Cell value styling */
.cell-value {
  color: #333;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
}

/* Button styling */
.button-small {
  padding: 6px 12px;
  font-size: 13px;
  white-space: nowrap;
}

/* Actions column alignment and width */
:deep(th:last-child),
:deep(td:last-child) {
  width: 1%;
  white-space: nowrap;
  text-align: right;
}
</style>
